const _excluded = ["as", "className", "items", "expandedContent", "expandedContentClassName", "rowId", "addPulse", "onClick", "size", "actionColumn", "disableShadow", "disableMarginBottom", "intent", "highlightOnHover", "toggleExpandProps", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FOCUS_DISABLED_CLASS } from "../../focus-management";
import { WideArrowUpward, WideArrowForward, WideArrowDownward } from "../../icons";
import { Card } from "../Card";
import { Checkbox } from "../Checkbox";
import { ConditionalWrapper } from "../ConditionalWrapper";
import { useDevice } from "../Device";
import { IconButton } from "../IconButton";
import { useIntentBehavior } from "../IntentBehavior";
import { FullRow } from "../Layout";
import { Typography } from "../Typography";
import styles from "./TableList.module.scss";
import { actionColumnDef, expandIconStyle, useTableListContext } from "./TableList.utils";
import { TableListItem } from "./TableListItem";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
let idCounter = 0;
const getID = () => {
  const id = (idCounter + 1) % (Number.MAX_SAFE_INTEGER - 1);
  idCounter = id;
  return id;
};
const TableListRowContext = /*#__PURE__*/React.createContext({
  setForceHover() {},
  size: 'medium'
});
export const useTableListRowContext = () => useContext(TableListRowContext);
export const TableListRowTestIds = {
  ToggleExpand: testid => "".concat(testid, "-toggle-expand")
};
const TableListRow = /*#__PURE__*/React.forwardRef(function TableListRow(props, ref) {
  const {
      as,
      className,
      items,
      expandedContent,
      expandedContentClassName,
      rowId,
      addPulse,
      onClick: _onClick,
      size = 'medium',
      actionColumn,
      disableShadow,
      disableMarginBottom,
      intent,
      highlightOnHover,
      toggleExpandProps = {},
      testId
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const {
    columnDefs,
    expandableRows,
    expandableRowsVariant = 'default',
    expandedRows,
    enableRowSelection,
    selectedRows,
    variant = 'default',
    hPadding,
    hasActionColumn,
    setSelectedRows,
    toggleExpandedRow,
    registerRow,
    unregisterRow,
    justifyBetween,
    disableMobile
  } = useTableListContext();
  const {
    isMobileDevice
  } = useDevice();
  const isMobileMode = isMobileDevice && !disableMobile;
  const [rowEl, setRowEl] = useState(null);
  const expandedContentRef = useRef(null);
  const {
    getDisabled
  } = useIntentBehavior();
  const disabledFromIntent = intent ? getDisabled(intent) : false;
  const [forceHover, setForceHover] = useState(false);
  const id = useMemo(() => rowId !== undefined ? rowId : getID(), [rowId]);
  const isExpanded = expandedRows[id] || false;
  const isSelected = selectedRows[id] || false;
  const RowElement = as || 'div';
  const contextValue = useMemo(() => ({
    setForceHover,
    size
  }), [size]);
  const isExpandableDefaultVariant = expandableRows && expandableRowsVariant === 'default';
  const isExpandableClickableIconAndRowVariant = expandableRows && expandableRowsVariant === 'clickableIconAndRow';
  const isExpandableClickableIconVariant = expandableRows && (expandableRowsVariant === 'clickableIcon' || isExpandableClickableIconAndRowVariant);
  useEffect(() => {
    if (toggleExpandProps.disabled) return;
    registerRow({
      rowId: id,
      isExpanded: isExpanded
    });
    return () => {
      unregisterRow(id);
    };
  }, [toggleExpandProps.disabled]);
  const resizeObs = useMemo(() => {
    return new ResizeObserver(entries => {
      if (!Array.isArray(entries) || !entries.length) return;
      for (const entry of entries) {
        requestAnimationFrame(() => {
          if (!entry.target || !expandedContentRef.current) return;
          expandedContentRef.current.style.width = "".concat(entry.target.scrollWidth, "px");
        });
      }
    });
  }, []);
  useEffect(() => {
    if (rowEl && isExpanded) {
      resizeObs.observe(rowEl);
      return () => resizeObs.unobserve(rowEl);
    }
  }, [rowEl, resizeObs, isExpanded]);
  const stickyItems = useMemo(() => items.filter((_, idx) => {
    var _columnDefs$idx;
    return (_columnDefs$idx = columnDefs[idx]) === null || _columnDefs$idx === void 0 ? void 0 : _columnDefs$idx.sticky;
  }), [items, columnDefs]);
  const stickyColumnDefs = useMemo(() => columnDefs.filter(col => col.sticky), [columnDefs]);
  const nonStickyItems = useMemo(() => items.filter((_, idx) => {
    var _columnDefs$idx2;
    return !((_columnDefs$idx2 = columnDefs[idx]) !== null && _columnDefs$idx2 !== void 0 && _columnDefs$idx2.sticky);
  }), [items, columnDefs]);
  const nonStickyColumnDefs = useMemo(() => columnDefs.filter(col => !col.sticky), [columnDefs]);
  if (isMobileMode) {
    const mobileItems = items.map((item, idx) => ({
      item,
      columnDef: columnDefs[idx]
    })).filter(_ref => {
      let {
        columnDef
      } = _ref;
      return (columnDef === null || columnDef === void 0 ? void 0 : columnDef.mobile) != null && !columnDef.mobile.hide;
    });
    return /*#__PURE__*/_jsx(TableListRowContext.Provider, {
      value: contextValue,
      children: /*#__PURE__*/_jsx(Card, {
        autoHeight: true,
        padding: "s",
        testId: testId,
        className: cn(styles.mobileCard, className),
        actions: hasActionColumn ? actionColumn : undefined,
        disableSeparator: true,
        children: /*#__PURE__*/_jsx("div", {
          children: mobileItems.map((_ref2, idx) => {
            var _columnDef$mobile;
            let {
              item,
              columnDef
            } = _ref2;
            return /*#__PURE__*/_jsx(ConditionalWrapper, {
              condition: idx === 0 && !!hasActionColumn,
              wrapper: children => /*#__PURE__*/_jsxs(FullRow, {
                alignItems: "center",
                justifyContent: "space-between",
                children: [/*#__PURE__*/_jsx("div", {
                  className: styles.mobileRowBeforeActions,
                  children: children
                }), /*#__PURE__*/_jsx("div", {
                  className: styles.mobileActions,
                  children: actionColumn
                })]
              }),
              children: /*#__PURE__*/_jsxs("div", {
                className: styles.mobileCardRow,
                "data-testid": testId && "".concat(testId, "-mobile-row-").concat(idx),
                children: [((_columnDef$mobile = columnDef.mobile) === null || _columnDef$mobile === void 0 ? void 0 : _columnDef$mobile.label) && /*#__PURE__*/_jsx(Typography, {
                  skin: "secondary",
                  className: styles.mobileCardRowLabel,
                  children: columnDef.mobile.label
                }), /*#__PURE__*/_jsx(Typography, {
                  skin: "secondary",
                  className: styles.mobileCardRowValue,
                  children: item.value
                })]
              })
            }, idx);
          })
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(TableListRowContext.Provider, {
    value: contextValue,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: setRowEl,
      className: cn(styles.row, styles["".concat(variant, "RowVariant")], styles["".concat(size, "RowSize")], {
        [styles.hover]: isExpanded && isExpandableDefaultVariant || forceHover,
        [styles.selected]: isSelected,
        [styles.pulse]: addPulse,
        [styles.disableShadow]: disableShadow,
        [styles.disableMarginBottom]: disableMarginBottom,
        [styles.highlightOnHover]: highlightOnHover
      }),
      children: [/*#__PURE__*/_jsxs(RowElement, _objectSpread(_objectSpread(_objectSpread({}, rest), testId != null ? {
        'data-testid': testId
      } : {}), {}, {
        ref: ref,
        "data-id": id,
        "data-checked": isSelected,
        disabled: disabledFromIntent,
        className: cn(styles.rowInner, styles["".concat(variant, "RowInnerVariant")], styles["".concat(size, "RowSize")], styles["".concat(hPadding, "Padding")], className, {
          [styles.clickable]: _onClick,
          [styles.justifyBetween]: justifyBetween
        }),
        onClick: event => {
          (isExpandableDefaultVariant || isExpandableClickableIconAndRowVariant) && !toggleExpandProps.disabled && toggleExpandedRow(id);
          _onClick && _onClick(event);
        },
        children: [(enableRowSelection || isExpandableClickableIconVariant || stickyItems.length > 0) && /*#__PURE__*/_jsxs("div", {
          className: cn(styles.stickyCheckboxWrapper, styles["".concat(hPadding, "Padding")]),
          children: [(enableRowSelection || isExpandableClickableIconVariant) && /*#__PURE__*/_jsxs("div", {
            className: cn(styles.stickyCheckbox, styles["".concat(hPadding, "Padding")]),
            onClick: e => e.stopPropagation(),
            children: [enableRowSelection && /*#__PURE__*/_jsx(Checkbox, {
              testId: testId && "".concat(testId, "-checkbox"),
              checked: isSelected,
              onChange: e => {
                setSelectedRows({
                  [id]: e.target.checked
                });
                // This prevents TableListActions to be visible if we click and select a row.
                if (document.body.classList.contains(FOCUS_DISABLED_CLASS)) {
                  e.target.blur();
                }
              }
            }), isExpandableClickableIconVariant && /*#__PURE__*/_jsx(IconButton, {
              size: "small",
              noPadding: true,
              testId: testId && TableListRowTestIds.ToggleExpand(testId),
              disabled: toggleExpandProps.disabled,
              tooltip: toggleExpandProps.tooltipText ? {
                title: toggleExpandProps.tooltipText,
                placement: 'right'
              } : undefined,
              onClick: () => toggleExpandedRow(id),
              children: isExpanded ? /*#__PURE__*/_jsx(WideArrowUpward, {
                width: 20,
                height: 20
              }) : /*#__PURE__*/_jsx(WideArrowDownward, {
                width: 20,
                height: 20
              })
            })]
          }), stickyItems.length > 0 && /*#__PURE__*/_jsx("div", {
            className: cn(styles.rowItemData, justifyBetween && styles.justifyBetween),
            children: stickyItems.map((item, idx) => /*#__PURE__*/_jsx(TableListItem, {
              columnDef: stickyColumnDefs[idx],
              item: item
            }, idx))
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: cn(styles.rowItemData, justifyBetween && styles.justifyBetween),
          children: nonStickyItems.map((item, idx) => /*#__PURE__*/_jsx(TableListItem, {
            columnDef: nonStickyColumnDefs[idx],
            item: item
          }, idx))
        }), (hasActionColumn || isExpandableDefaultVariant) && /*#__PURE__*/_jsxs("div", {
          className: cn(styles.rowActionSticky, styles["".concat(hPadding, "Padding")]),
          children: [hasActionColumn && /*#__PURE__*/_jsx(TableListItem, {
            columnDef: actionColumnDef,
            item: {
              value: actionColumn || ''
            }
          }), isExpandableDefaultVariant && /*#__PURE__*/_jsx("div", {
            style: expandIconStyle,
            children: isExpanded ? /*#__PURE__*/_jsx(WideArrowUpward, {}) : /*#__PURE__*/_jsx(WideArrowForward, {})
          })]
        })]
      })), isExpanded && isExpandableDefaultVariant && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.separator
        }), expandedContent && expandedContent()]
      })]
    }), isExpanded && isExpandableClickableIconVariant && expandedContent && /*#__PURE__*/_jsx("div", {
      ref: expandedContentRef,
      className: cn(styles.expandedContent, expandedContentClassName),
      children: expandedContent()
    })]
  });
});
TableListRow.displayName = 'TableListRow';
export { TableListRow };